import type { SessionId } from "@don/wire";

/**
 * The session timeline: epochs, gaps, and the arithmetic the player and the scrubber run
 * on. Pure, and none of it lives in a component.
 *
 * Three product rules are the reason this file exists rather than a `useMemo` inside
 * `SessionScreen`:
 *
 * 1. **A gap always carries its reason** (`CONTEXT.md`). The enforceable form of that is
 *    not "remember to pass one". It is that an unattributed hole in the timeline is not a
 *    value anything downstream can be handed. `timelineOf` refuses one, so the scrubber
 *    cannot render a gap the owner would read as a bug.
 * 2. **One playlist per media epoch.** hls.js mishandles multiple `EXT-X-MAP` tags in one
 *    playlist, and a new epoch means new encoder parameters (docking, rotating, a
 *    resolution change mid-session), so an epoch boundary is a real discontinuity and the
 *    player swaps source there (architecture §2.8). Which epoch holds a position, and what
 *    offset inside it that position is, is arithmetic, and arithmetic is worth asserting
 *    without a browser.
 * 3. **Media time is not timeline time.** A gap produces no fragments, and an HLS
 *    playlist's duration is the sum of its `EXTINF`s, so seeking to 20 minutes into a
 *    session that paused for 4 of them is not seeking to 20 minutes into the media.
 *
 * **This is not `wire::SessionManifest`.** That type carries tracks, epochs, fragments,
 * source fidelity and artifacts, and no gaps. `GapReason` lives in `crates/session-clock`
 * and reaches neither the wire nor any migration, so there is nothing to fetch yet and the
 * shape below is the port a real adapter fills. The five reasons are spelled exactly as
 * `GapReason::as_str` spells them, so the day the wire carries a gap there is one
 * vocabulary rather than two.
 */

/**
 * Why the timeline has a hole here.
 *
 * `crates/session-clock/src/gaps.rs`'s five, under `as_str`'s spellings. A closed union
 * rather than a string: the point of the type is that a hole cannot be unattributed.
 */
export type GapReason =
  | "capture_lost"
  | "screen_locked"
  | "machine_asleep"
  | "disk_full"
  | "paused";

/** The cause half of a gap's one form, `4 min · paused by Maricel` (P5, design-spec §6). */
const GAP_LABELS: Readonly<Record<GapReason, string>> = {
  capture_lost: "capture lost",
  screen_locked: "screen locked",
  machine_asleep: "computer asleep",
  disk_full: "disk full",
  paused: "paused by the person recording",
};

/** The five, as values, so a walk over the vocabulary is the vocabulary. */
export const GAP_REASONS = Object.keys(GAP_LABELS) as readonly GapReason[];

/**
 * Whether this is one of the five, and `includes` rather than `in` for a reason.
 *
 * `in` walks the prototype chain, so `"constructor"`, `"toString"` and `"__proto__"` are
 * all `in` an object literal. Written that way this closed nothing, and a gap carrying an
 * inherited name reached the scrubber as `GAP · 5:00 · function Object() { [native code] }`,
 * which is `CONTEXT.md`'s rule inverted: a hole with a fabricated reason. The vocabulary is
 * these five values, so membership is a walk over them.
 */
export function isGapReason(value: unknown): value is GapReason {
  return typeof value === "string" && (GAP_REASONS as readonly string[]).includes(value);
}

/**
 * What the owner reads on a gap.
 *
 * The Console's own copy rather than the design system's `GapMarker`: that component's
 * reason table holds four keys (`paused`, `locked`, `blocked`, `asleep`) and falls through
 * to the raw string for anything else, so `capture_lost` would reach the screen as
 * `capture_lost`. It is also a chip and a scrubber gap is a segment of a rail. Widening it
 * would make it stop being a port of what was shipped, which is issue 25's Decision 3.
 */
export function gapLabel(reason: GapReason, person?: string): string {
  // Only the person recording can pause their own recorder, so a pause names them, by the
  // first word of their name as variant E does (`paused by Maricel`).
  const first = person?.trim().split(/\s+/)[0];
  if (reason === "paused" && first) return `paused by ${first}`;
  return GAP_LABELS[reason];
}

/**
 * A gap's length in its one form: under a minute in whole seconds (`40 s`), otherwise in
 * whole minutes (`4 min`), never less than one of either.
 */
export function gapSpan(ms: number): string {
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  return `${Math.max(1, Math.round(ms / 60_000))} min`;
}

/** A gap as the owner reads it: `4 min · paused by Maricel` (P5, one form everywhere). */
export function gapText(gap: { startMs: number; endMs: number; reason: GapReason }, person?: string): string {
  return `${gapSpan(gap.endMs - gap.startMs)} · ${gapLabel(gap.reason, person)}`;
}

/** One attributed hole. `endMs` is exclusive, as `session_clock::Gap` has it. */
export interface SessionGap {
  readonly reason: GapReason;
  readonly startMs: number;
  readonly endMs: number;
}

/**
 * A run of fragments sharing encoder parameters, placed on the session timeline.
 *
 * `playlist` is the URL of this epoch's own `playlist.m3u8`, one per epoch, per
 * architecture §2.8. `width` and `height` are here because they are what makes an epoch
 * boundary visible to a reader: a mid-session resolution change is the case issue 08
 * produces and this screen has to survive.
 */
export interface SessionEpoch {
  readonly trackIndex: number;
  readonly ordinal: number;
  /** Where this epoch begins on the session timeline. */
  readonly startMs: number;
  /** How much of the session timeline it covers, gaps inside it included. */
  readonly durationMs: number;
  /**
   * How much media it actually holds: the sum of its playlist's `EXTINF`s.
   *
   * **This is what makes an unattributed gap an arithmetic contradiction rather than a
   * blank space.** A gap produces no fragments, so a stretch of timeline longer than the
   * media inside it is a stretch with holes in it, and `timelineOf` refuses the manifest
   * unless every one of those milliseconds is claimed by a reason. Carrying only the
   * timeline span would leave a gap inside an epoch a thing the Console could silently
   * fail to draw.
   */
  readonly mediaDurationMs: number;
  readonly playlist: string;
  readonly width: number;
  readonly height: number;
}

/**
 * One captured track. `kind` is `wire::MediaKind`'s spelling; `stills` is the degraded
 * machine, which reads as a lesser artifact rather than a broken one.
 */
export interface SessionTrack {
  readonly index: number;
  /** The display as the machine named it, e.g. `Left 27"`. */
  readonly label: string;
  readonly kind: "video" | "stills";
}

/** What the session-detail screen plays. The port a real adapter fills. */
export interface SessionMedia {
  readonly sessionId: SessionId;
  /** The whole session timeline, gaps included. */
  readonly durationMs: number;
  readonly tracks: readonly SessionTrack[];
  readonly epochs: readonly SessionEpoch[];
  /** Session-wide: capture stopping stops every track. */
  readonly gaps: readonly SessionGap[];
}

/** One piece of the scrubber. A `gap` segment always carries a reason. */
export type Segment =
  | { readonly kind: "media"; readonly startMs: number; readonly endMs: number }
  | {
      readonly kind: "gap";
      readonly startMs: number;
      readonly endMs: number;
      readonly reason: GapReason;
    };

/** A track's timeline, validated. Nothing constructs one but `timelineOf`. */
export interface Timeline {
  readonly trackIndex: number;
  readonly durationMs: number;
  readonly epochs: readonly SessionEpoch[];
  readonly gaps: readonly SessionGap[];
  readonly segments: readonly Segment[];
}

/**
 * Why a timeline was refused.
 *
 * A value rather than a thrown error: the screen renders the refusal, and a render that
 * threw would take the rest of the session detail with it. `detail` names the interval so
 * the developer who handed it over can find the hole.
 */
export interface TimelineFault {
  readonly fault:
    | "no-track"
    | "unattributed-hole"
    | "epoch-overruns-media"
    | "gaps-overrun-epoch"
    | "unknown-gap-reason"
    | "overlapping-epochs"
    | "non-finite"
    | "fractional-ms"
    | "empty-gap"
    | "outside-session"
    | "not-a-partition"
    | "empty";
  readonly detail: string;
}

export function isTimeline(value: Timeline | TimelineFault): value is Timeline {
  return !("fault" in value);
}

/**
 * The track's timeline, or why there is not one.
 *
 * **The refusal is the point, and there is exactly one rule behind all of them.**
 *
 * > **The invariant.** The returned `segments` tile `[0, durationMs)` once: the first
 * > begins at 0, each begins where the previous ended, none is empty, the last ends at
 * > `durationMs`, every `gap` segment carries one of the recorder's five reasons, and the
 * > measure of the `media` segments equals the sum of this track's epochs'
 * > `mediaDurationMs`.
 *
 * > **The precondition.** Every millisecond on the input is a whole number, which step 1
 * > refuses a value that is not. The measure clause above is an exact equality on IEEE
 * > doubles, so a fractional input makes it a test of rounding: `600000.3` spread over an
 * > epoch and two gaps refused a well-formed manifest for being "missing
 * > 7.275957614183426e-11ms nothing explains". The wire carries milliseconds as `u64`, so
 * > the whole-number domain is what a real adapter fills, and stating it is cheaper than
 * > an epsilon nobody can pick.
 *
 * Everything below is either admitting the inputs that can satisfy it, building the value,
 * or checking that the built value does. The last of those three is what makes this total
 * over that domain: step 3 runs on the value about to be returned rather than on the shapes
 * anyone thought to enumerate, so a manifest nobody predicted is refused rather than drawn.
 *
 * **Why that shape rather than another guard.** Three consecutive reviews of issue 28 each
 * found a *different* malformed manifest reaching a printed assurance, and each was closed
 * by a check written for it. The last one measured why the class kept regenerating: the
 * per-epoch attribution clipped gaps to the **epoch's** span while the segment builder
 * clipped them to the **session's**, so a gap in the difference paid for media missing
 * inside the session and the rail drew solid over it. Two windows, so any input that tells
 * them apart is a defect. Step 1 refuses an epoch or a gap that leaves `[0, durationMs]`,
 * which makes the two windows the same window by construction; step 3 catches whatever
 * that reasoning missed, by arithmetic rather than by anticipation.
 *
 * The named faults above `not-a-partition` are kept because `detail` is what a developer
 * reads; `not-a-partition` is the backstop, and reaching it means one of them should have
 * fired first.
 */
export function timelineOf(
  media: SessionMedia,
  trackIndex: number,
): Timeline | TimelineFault {
  if (!media.tracks.some((track) => track.index === trackIndex)) {
    return { fault: "no-track", detail: `no track ${trackIndex} in this session` };
  }

  // --- 1. The domain. One pass, and nothing below it sees a number this did not admit.
  //
  // `NaN` compares false against every ordering, so an unguarded non-finite walks through
  // `missing < 0`, `attributed > missing` and `attributed < missing` alike and is accepted
  // with no gaps. `mediaDurationMs` is the field the wire does not carry yet, so a
  // half-filled adapter is the ordinary producer of that shape, not a hostile one.
  if (!Number.isFinite(media.durationMs)) {
    return { fault: "non-finite", detail: `session durationMs is ${media.durationMs}` };
  }
  if (!Number.isInteger(media.durationMs)) {
    return { fault: "fractional-ms", detail: whole("session durationMs", media.durationMs) };
  }
  if (media.durationMs <= 0) {
    return { fault: "empty", detail: "a session with no duration has no timeline" };
  }
  const sessionEndMs = media.durationMs;

  for (const gap of media.gaps) {
    if (!isGapReason(gap.reason)) {
      return {
        fault: "unknown-gap-reason",
        detail: `gap at ${gap.startMs}ms carries ${JSON.stringify(gap.reason)}`,
      };
    }
    for (const field of ["startMs", "endMs"] as const) {
      if (!Number.isFinite(gap[field])) {
        return { fault: "non-finite", detail: `gap ${field} is ${gap[field]}` };
      }
      if (!Number.isInteger(gap[field])) {
        return { fault: "fractional-ms", detail: whole(`gap ${field}`, gap[field]) };
      }
    }
    // A gap outside the session is the shape that paid for a hole inside it. Refused
    // rather than clipped: clipping is what produced two windows.
    if (gap.startMs < 0 || gap.endMs > sessionEndMs) {
      return {
        fault: "outside-session",
        detail: `gap ${gap.startMs}ms\u2013${gap.endMs}ms is not inside 0ms\u2013${sessionEndMs}ms`,
      };
    }
    // Its own branch, because it was folded into the bounds check above and reported a gap
    // at `100000ms\u2013100000ms` inside a ten-minute session as being outside it. A fault
    // that misnames its own cause is the one thing this design cannot absorb: `detail` is
    // the whole reason the named faults exist above `not-a-partition`.
    if (gap.endMs <= gap.startMs) {
      return {
        fault: "empty-gap",
        detail: `gap ${gap.startMs}ms\u2013${gap.endMs}ms covers no time`,
      };
    }
  }

  const epochs = media.epochs
    .filter((epoch) => epoch.trackIndex === trackIndex)
    .slice()
    .sort((a, b) => a.startMs - b.startMs || a.ordinal - b.ordinal);

  // A track with no epoch has no playlist to load, whatever its gaps claim. Refused here so
  // that every `Timeline` has at least one epoch and `epochFor` can be total.
  if (epochs.length === 0) {
    return { fault: "empty", detail: `track ${trackIndex} has no media to play` };
  }
  for (const each of epochs) {
    for (const field of ["startMs", "durationMs", "mediaDurationMs"] as const) {
      if (!Number.isFinite(each[field])) {
        return {
          fault: "non-finite",
          detail: `epoch ${each.ordinal} ${field} is ${each[field]}`,
        };
      }
      if (!Number.isInteger(each[field])) {
        return {
          fault: "fractional-ms",
          detail: whole(`epoch ${each.ordinal} ${field}`, each[field]),
        };
      }
    }
    if (
      each.startMs < 0 ||
      each.durationMs <= 0 ||
      each.mediaDurationMs < 0 ||
      each.startMs + each.durationMs > sessionEndMs
    ) {
      return {
        fault: "outside-session",
        detail: `epoch ${each.ordinal} spans ${each.startMs}ms\u2013${
          each.startMs + each.durationMs
        }ms, outside 0ms\u2013${sessionEndMs}ms`,
      };
    }
  }

  for (let i = 1; i < epochs.length; i += 1) {
    const previous = epochs[i - 1]!;
    const next = epochs[i]!;
    if (next.startMs < previous.startMs + previous.durationMs) {
      return {
        fault: "overlapping-epochs",
        detail: `epoch ${next.ordinal} starts inside epoch ${previous.ordinal}`,
      };
    }
  }

  const gaps = media.gaps.slice().sort((a, b) => a.startMs - b.startMs);

  // --- 2. The value. Whatever an epoch does not cover, a gap must.
  const segments: Segment[] = [];
  let at = 0;
  const holes: { startMs: number; endMs: number }[] = [];
  for (const epoch of epochs) {
    if (epoch.startMs > at) holes.push({ startMs: at, endMs: epoch.startMs });
    at = Math.max(at, epoch.startMs + epoch.durationMs);
  }
  if (at < sessionEndMs) holes.push({ startMs: at, endMs: sessionEndMs });

  // Each epoch's own arithmetic: the timeline it spans, less the media it holds, is time
  // that produced no fragments, and every millisecond of that must be attributed. Both
  // directions, and `attributed` is computed before either is decided: an earlier draft
  // skipped the whole check when an epoch's media exactly filled its span, which made the
  // rule hold one way only and drew a hatch over media that exists.
  for (const each of epochs) {
    const missing = each.durationMs - each.mediaDurationMs;
    const attributed = measureWithin(gaps, each.startMs, each.startMs + each.durationMs);
    // Two conditions and two sentences, because they are two different manifests. `missing
    // < 0` is an epoch whose playlist is longer than the timeline it claims. `attributed >
    // missing` is the gaps claiming time the epoch is not missing, which is what two
    // overlapping gaps inside one epoch produce, and there the epoch holds *less* media
    // than its span: one sentence for both pointed a developer at the wrong half.
    if (missing < 0) {
      return {
        fault: "epoch-overruns-media",
        detail: `epoch ${each.ordinal} holds ${each.mediaDurationMs}ms of media over ${each.durationMs}ms of timeline`,
      };
    }
    if (attributed > missing) {
      return {
        fault: "gaps-overrun-epoch",
        detail: `epoch ${each.ordinal} is missing ${missing}ms and its gaps claim ${attributed}ms of it`,
      };
    }
    if (attributed < missing) {
      return {
        fault: "unattributed-hole",
        detail: `epoch ${each.ordinal} is missing ${missing - attributed}ms nothing explains`,
      };
    }
  }

  for (const hole of holes) {
    const attributed = measureWithin(gaps, hole.startMs, hole.endMs);
    if (attributed < hole.endMs - hole.startMs) {
      return {
        fault: "unattributed-hole",
        detail: `nothing explains ${hole.startMs}ms\u2013${hole.endMs}ms`,
      };
    }
  }

  let cursor = 0;
  for (const gap of gaps) {
    const start = Math.max(cursor, gap.startMs);
    const end = Math.min(sessionEndMs, gap.endMs);
    if (end <= start) continue;
    if (start > cursor) segments.push({ kind: "media", startMs: cursor, endMs: start });
    segments.push({ kind: "gap", startMs: start, endMs: end, reason: gap.reason });
    cursor = end;
  }
  if (cursor < sessionEndMs) {
    segments.push({ kind: "media", startMs: cursor, endMs: sessionEndMs });
  }

  // --- 3. The invariant, checked on the value rather than on the inputs.
  //
  // This is the total half. It does not know which shape produced the segments, so it
  // refuses a shape nobody enumerated: two gaps overlapping each other, for instance, are
  // double-counted by the measures above and can add up to exactly the media an epoch says
  // is missing, which every check in step 2 accepts and this one does not.
  let tiled = 0;
  for (const segment of segments) {
    if (segment.startMs !== tiled || segment.endMs <= segment.startMs) {
      return {
        fault: "not-a-partition",
        detail: `a segment at ${segment.startMs}ms\u2013${segment.endMs}ms does not follow ${tiled}ms`,
      };
    }
    // The reason clause of the invariant, checked where the rest of it is: on the value.
    // `unknown-gap-reason` in step 1 is what refuses this in practice, and this is the
    // backstop that makes the sentence above true of what is returned rather than true of
    // what was admitted. Reaching it means step 1 should have fired first.
    if (segment.kind === "gap" && !isGapReason(segment.reason)) {
      return {
        fault: "not-a-partition",
        detail: `the gap at ${segment.startMs}ms carries ${JSON.stringify(segment.reason)}`,
      };
    }
    tiled = segment.endMs;
  }
  if (tiled !== sessionEndMs) {
    return {
      fault: "not-a-partition",
      detail: `the segments cover ${tiled}ms of a ${sessionEndMs}ms session`,
    };
  }
  const drawn = segments.reduce(
    (total, segment) => total + (segment.kind === "media" ? segment.endMs - segment.startMs : 0),
    0,
  );
  const held = epochs.reduce((total, each) => total + each.mediaDurationMs, 0);
  if (drawn !== held) {
    return {
      fault: "not-a-partition",
      detail: `the rail draws ${drawn}ms of media over ${held}ms the playlists hold`,
    };
  }

  return { trackIndex, durationMs: sessionEndMs, epochs, gaps, segments };
}

/**
 * The `fractional-ms` detail, written once so every field refuses the same way.
 *
 * It names the value, because the sentence a developer gets otherwise is the arithmetic's
 * own residue: `missing 7.275957614183426e-11ms nothing explains` describes the rounding
 * rather than the manifest.
 */
function whole(field: string, value: number): string {
  return `${field} is ${value}, and a millisecond is a whole number`;
}

/** How much of `[fromMs, toMs)` these gaps claim, each clipped to that window. */
function measureWithin(
  gaps: readonly SessionGap[],
  fromMs: number,
  toMs: number,
): number {
  return gaps.reduce((total, gap) => {
    const start = Math.max(gap.startMs, fromMs);
    const end = Math.min(gap.endMs, toMs);
    return total + Math.max(0, end - start);
  }, 0);
}

/** Which epoch holds `positionMs`, or `null` where the timeline has a gap there. */
export function epochAt(timeline: Timeline, positionMs: number): SessionEpoch | null {
  return (
    timeline.epochs.find(
      (epoch) =>
        positionMs >= epoch.startMs && positionMs < epoch.startMs + epoch.durationMs,
    ) ?? null
  );
}

/**
 * The epoch the player should load for a position. Always one.
 *
 * `epochAt` is half-open, so it answers `null` at the very end of the timeline and inside a
 * gap that no epoch covers. The player has to load a playlist in both cases, and the one to
 * load is the last epoch that has begun, not the first epoch of the session. Falling back to
 * `epochs[0]` sent an owner who pressed `End` on a two-epoch recording to the end of its
 * *first* epoch, which is the wrong end of the recording and reads as a seek that was
 * ignored.
 */
export function epochFor(timeline: Timeline, positionMs: number): SessionEpoch {
  const held = epochAt(timeline, positionMs);
  if (held) return held;
  // `timelineOf` sorts by `startMs` and refuses a track with no epochs, so this walk is over
  // a non-empty ascending list and the first element is the floor.
  let latest = timeline.epochs[0]!;
  for (const epoch of timeline.epochs) {
    if (epoch.startMs <= positionMs) latest = epoch;
  }
  return latest;
}

/**
 * Where in `epoch`'s media a timeline position sits.
 *
 * The gap time already passed inside this epoch is subtracted, because a gap produces no
 * fragments and the playlist's own clock is the sum of its `EXTINF`s. Without this, a
 * session that paused for four minutes would seek four minutes past what the owner
 * clicked, and would do it more the further in they clicked.
 */
export function mediaOffsetMs(
  timeline: Timeline,
  epoch: SessionEpoch,
  positionMs: number,
): number {
  const clamped = Math.min(
    Math.max(positionMs, epoch.startMs),
    epoch.startMs + epoch.durationMs,
  );
  return clamped - epoch.startMs - measureWithin(timeline.gaps, epoch.startMs, clamped);
}

/**
 * The inverse: the timeline position an epoch's media offset is at.
 *
 * What the player reports while it plays. Walks the epoch's gaps forward rather than
 * inverting the subtraction, because the mapping is a step function: the media instant a
 * gap begins at is reached from both sides of the gap. The tie goes to the *start*, so the
 * playhead sits on the near edge of the hatch and then jumps it, rather than appearing
 * past it before any of it has been played.
 */
export function positionOf(
  timeline: Timeline,
  epoch: SessionEpoch,
  mediaMs: number,
): number {
  let position = epoch.startMs;
  let remaining = Math.max(0, mediaMs);
  const inside = timeline.gaps
    .filter((gap) => gap.startMs >= epoch.startMs && gap.startMs < epoch.startMs + epoch.durationMs)
    .slice()
    .sort((a, b) => a.startMs - b.startMs);
  for (const gap of inside) {
    const before = gap.startMs - position;
    if (remaining <= before) break;
    remaining -= before;
    position = gap.endMs;
  }
  return Math.min(position + remaining, epoch.startMs + epoch.durationMs);
}

/**
 * Where a click at `fraction` of the rail seeks to.
 *
 * A click inside a gap lands on the gap's end. There is no frame inside a gap, so seeking
 * into one would leave the player on the last frame before it while the playhead sat in
 * the middle of a hatch, which reads as a stall.
 */
export function seekTargetMs(timeline: Timeline, fraction: number): number {
  const raw = Math.min(Math.max(fraction, 0), 1) * timeline.durationMs;
  // `>` and not `>=`: the instant a gap begins is the last frame before it, which exists.
  const gap = timeline.gaps.find((each) => raw > each.startMs && raw < each.endMs);
  const target = gap ? gap.endMs : raw;
  return Math.min(Math.max(0, Math.round(target)), timeline.durationMs);
}

/** `14:22`, and `1:07:40` once there is an hour of it. */
export function timecode(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
